import React, { useState } from "react";
import { Button, CircularProgress, Box } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";


const VALID_SIZES =
/** @type {const} */ ([
    "md",
    "medium",
    "sm",
    "small",
    "table-bar-button",
    "dialog-button",
  ]);

const VALID_VARIANTS =
/** @type {const} */ ([
    "primary",
    "secondary",
    "text",
    "text-only",
    "bordered",
    "auth-button",
    "adornment-button",
  ]);

const LEGACY_VARIANT_MAP = {
  "normal-button": "primary",
  "outline-normal-button": "bordered",
  "table-bordered-button": "bordered",
};

/**
 * 
 * @param {Object} props
 * @param {React.ReactNode} [props.label] 
 * @param {React.ReactNode} [props.children] 
 * @param {React.ReactNode} [props.startIcon] 
 * @param {React.ReactNode} [props.endIcon] 
 * @param {boolean} [props.isLoading] 
 * @param {typeof VALID_SIZES[number]} props.size
 * @param {typeof VALID_VARIANTS[number]} props.variant
 * @param {boolean} [props.disabled] 
 * @param {(event: React.MouseEvent) => void | Promise<void>} [props.onClick] 
 * @param {string} [props.className] 
 * @param {boolean} [props.smallWidth] 
 * @param {boolean} [props.nowrap] 
 * @param {import('@mui/material/styles').SxProps} [props.sx] 
 * @returns 
 */
const ButtonMAS = ({
  label,
  startIcon,
  endIcon,
  isLoading,
  children,
  size,
  variant,
  disabled,
  onClick,
  className,
  smallWidth,
  nowrap,
  sx,
  ...props
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const effectiveLoading =
    isLoading !== undefined ? isLoading : internalLoading;
  const hasText =
    !!label || typeof children === "string" || typeof children === "number";
  const hasChildren = children !== undefined && children !== null;
  const useOverlayLoading =
    effectiveLoading && hasText && !startIcon && !endIcon;

  const buttonSize = VALID_SIZES.includes(size?.toLowerCase()) ? size : "md";
  const normalizedVariant = variant?.toLowerCase();
  const buttonVariant =
    LEGACY_VARIANT_MAP[normalizedVariant] ||
    (VALID_VARIANTS.includes(normalizedVariant) ? normalizedVariant : "primary");

  const rootSx = [
    smallWidth && {
      paddingLeft: "2px",
      paddingRight: "2px",
    },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ].filter(Boolean);

  const loadingPosition = startIcon ? "start" : endIcon ? "end" : "center";
  const loadingIconPlaceholder = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        width: "1em",
        height: "1em",
        visibility: "hidden",
      }}
    />
  );

  const buttonContent = hasText ? (
    <Box
      component="span"
      className="button-mas-label"
      sx={nowrap ? { whiteSpace: "nowrap" } : undefined}
    >
      {label || children}
    </Box>
  ) : hasChildren ? (
    children
  ) : null;

  const handleClick = async (event) => {
    if (!onClick || disabled || effectiveLoading) return;

    const result = onClick(event);
    if (
      isLoading === undefined &&
      result &&
      typeof result.then === "function"
    ) {
      setInternalLoading(true);
      try {
        await result;
      } finally {
        setInternalLoading(false);
      }
    }
  };

  const getLoadingSafeIcon = (icon) => {
    if (!icon) return undefined;
    return effectiveLoading ? loadingIconPlaceholder : icon;
  };

  const sharedButtonProps = {
    className,
    variant: buttonVariant,
    size: buttonSize,
    disableRipple: true,
    "data-loading": effectiveLoading,
    "data-loading-overlay": useOverlayLoading,
    sx: rootSx,
    ...props,
  };

  return (
    <>
      {useOverlayLoading ? (
        <Button
          {...sharedButtonProps}
          disabled={disabled || effectiveLoading}
          onClick={handleClick}
        >
          {buttonContent}
          <Box className="button-mas-overlay-spinner">
            <CircularProgress size="1em" color="inherit" />
          </Box>
        </Button>
      ) : (
        <LoadingButton
          {...sharedButtonProps}
          disabled={disabled}
          loading={effectiveLoading}
          loadingPosition={loadingPosition}
          loadingIndicator={
            <Box className="button-mas-spinner">
              <CircularProgress size="1em" color="inherit" />
            </Box>
          }
          onClick={handleClick}
          startIcon={getLoadingSafeIcon(startIcon)}
          endIcon={getLoadingSafeIcon(endIcon)}
        >
          {buttonContent}
        </LoadingButton>
      )}
    </>
  );
};

export default ButtonMAS;
